import React, { useState, useEffect } from 'react';
import { reportApi, projectApi } from '../services/api';
import { Card, Select, Spinner, Badge } from '../components/UI';
import { Flag, ListChecks, Warning, ChartBar } from '@phosphor-icons/react';

import { fmtCurrencyShort } from '../utils/currencyFormat';

export default function ProjectDashboardPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { projectApi.list().then(r => { setProjects(r.data); if (r.data.length) setSelectedProject(r.data[0].id); }).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    if (!selectedProject) return;
    reportApi.projectDashboard(selectedProject).then(r => setData(r.data));
  }, [selectedProject]);

  const fmt = (n) => fmtCurrencyShort(n);

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="project-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Project Dashboard</h1>
          <p className="text-sm text-[#666]">Milestones, tasks, progress & cost per project</p>
        </div>
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-64" data-testid="select-project">
          <option value="">-- Select Project --</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {!data ? (
        <Card className="p-8 text-center text-[#666]">Select a project to view dashboard</Card>
      ) : (
        <>
          {/* Project Info */}
          <Card className="p-4" data-testid="project-info">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <p className="text-lg font-bold text-[#0A0A0A]">{data.project.name}</p>
                <p className="text-sm text-[#666]">{data.project.client_name} — {data.project.location}</p>
              </div>
              <Badge variant={data.project.status === 'ACTIVE' ? 'success' : 'default'}>{data.project.status}</Badge>
              <div className="ml-auto text-right">
                <p className="text-[10px] text-[#999] uppercase">Contract Value</p>
                <p className="text-xl font-bold text-[#002FA7]">{fmt(data.contract_value)}</p>
              </div>
            </div>
          </Card>

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="project-kpis">
            <Card className="p-3 text-center">
              <Flag className="w-5 h-5 mx-auto mb-1 text-[#FFAB00]" />
              <p className="text-[10px] text-[#999] uppercase">Milestones</p>
              <p className="text-xl font-bold">{data.milestones.completed}/{data.milestones.total}</p>
            </Card>
            <Card className="p-3 text-center">
              <ListChecks className="w-5 h-5 mx-auto mb-1 text-[#002FA7]" />
              <p className="text-[10px] text-[#999] uppercase">Tasks Done</p>
              <p className="text-xl font-bold text-[#00C853]">{data.tasks.completion_pct}%</p>
              <p className="text-[10px] text-[#666]">{data.tasks.completed}/{data.tasks.total}</p>
            </Card>
            <Card className="p-3 text-center">
              <Warning className="w-5 h-5 mx-auto mb-1 text-[#FF3333]" />
              <p className="text-[10px] text-[#999] uppercase">Delayed</p>
              <p className="text-xl font-bold text-[#FF3333]">{data.tasks.delayed}</p>
            </Card>
            <Card className="p-3 text-center">
              <ChartBar className="w-5 h-5 mx-auto mb-1 text-[#FF3333]" />
              <p className="text-[10px] text-[#999] uppercase">Total Cost</p>
              <p className="text-xl font-bold">{fmt(data.costs.total)}</p>
            </Card>
          </div>

          {/* Task progress bar */}
          <Card className="p-4" data-testid="task-progress">
            <h3 className="text-sm font-semibold mb-2">Task Progress</h3>
            <div className="w-full bg-[#E5E5E5] rounded-full h-4 overflow-hidden">
              <div className="bg-[#00C853] h-full rounded-full transition-all" style={{ width: `${data.tasks.completion_pct}%` }}></div>
            </div>
            <div className="flex justify-between mt-1 text-xs text-[#666]">
              <span>{data.tasks.completed} completed</span>
              <span>{data.tasks.in_progress} in progress</span>
              <span>{data.tasks.total - data.tasks.completed - data.tasks.in_progress} pending</span>
            </div>
          </Card>

          {/* Milestones List */}
          {data.milestones.data.length > 0 && (
            <Card className="p-4" data-testid="milestone-list">
              <h3 className="text-sm font-semibold mb-2">Milestones</h3>
              <div className="space-y-2">
                {data.milestones.data.map(m => (
                  <div key={m.id} className="flex items-center justify-between py-1 border-b border-[#F0F0F0]">
                    <div>
                      <span className="text-sm font-medium">{m.name}</span>
                      <span className="text-xs text-[#666] ml-2">{m.planned_date ? new Date(m.planned_date).toLocaleDateString() : '-'}</span>
                    </div>
                    <Badge variant={m.status === 'COMPLETED' ? 'success' : m.status === 'IN_PROGRESS' ? 'warning' : 'default'}>{m.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Cost Breakdown */}
          {Object.keys(data.costs.by_type).length > 0 && (
            <Card className="p-4" data-testid="cost-breakdown">
              <h3 className="text-sm font-semibold mb-2">Cost Breakdown</h3>
              {Object.entries(data.costs.by_type).map(([type, total]) => (
                <div key={type} className="flex items-center justify-between py-1">
                  <span className="text-sm capitalize">{type}</span>
                  <span className="font-bold text-sm">{fmt(total)}</span>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
